// WebinarDetails.jsx
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Divider,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  LinearProgress,
  useMediaQuery,
  useTheme,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  Fade,
  Zoom,
  Badge
} from '@mui/material';
import {
  ArrowBack,
  CalendarToday,
  AccessTime,
  LocationOn,
  Language,
  Person,
  Timer,
  TrendingUp,
  Chair,
  PlayCircle,
  CheckCircle,
  Share,
  Favorite,
  FavoriteBorder,
  EventNote,
  Payment,
  Close,
  WhatsApp,
  Twitter,
  LinkedIn,
  Email,
  FileCopy,
  Star,
  StarBorder,
  School,
  WorkspacePremium,
  Info,

} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { webinarData } from './Tectaks';

const WebinarDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // State variables
  const [registerOpen, setRegisterOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    designation: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isFavorite, setIsFavorite] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Find webinar
  const allWebinars = [
    ...webinarData.Mechanical,
    ...webinarData.IT
  ];
  const webinar = allWebinars.find(item => item.id === parseInt(id));

  // Level badge configuration
  const getLevelBadge = (level) => {
    const levels = {
      basic: { color: '#10b981', label: 'Basic', icon: <School /> },
      advanced: { color: '#f59e0b', label: 'Advanced', icon: <TrendingUp /> },
      expert: { color: '#ef4444', label: 'Expert', icon: <WorkspacePremium /> }
    };
    return levels[level] || levels.basic;
  };

  if (!webinar) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>Webinar Not Found</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            The webinar you're looking for doesn't exist.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            Back to Webinars
          </Button>
        </Paper>
      </Container>
    );
  }

  const levelInfo = getLevelBadge(webinar.level);

  // Handle registration form
  const handleRegisterClick = () => {
    if (isRegistered) {
      setSnackbar({
        open: true,
        message: 'You are already registered for this webinar!',
        severity: 'info'
      });
      return;
    }
    setRegisterOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone)) {
      errors.phone = 'Invalid phone number';
    }
    return errors;
  };

  const handleRegisterSubmit = () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setRegisterOpen(false);
    setPaymentOpen(true);
  };

  // Razorpay payment integration
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      setSnackbar({
        open: true,
        message: 'Failed to load payment gateway. Please try again.',
        severity: 'error'
      });
      return;
    }

    const options = {
      key: 'rzp_test_R6hte6Puir9RAR', // Replace with your actual test key
      amount: webinar[0]?.cost === 'Free' ? 100 : 50000, // Amount in paise (e.g., ₹500)
      currency: 'INR',
      name: webinar.title,
      description: webinar.subtitle,
      image: webinar.image,
      prefill: {
        name: formData.name,
        email: formData.email,
        contact: formData.phone
      },
      notes: {
        webinar_id: webinar.id,
        webinar_title: webinar.title
      },
      theme: {
        color: '#2196f3'
      },
      modal: {
        ondismiss: () => {
          setSnackbar({
            open: true,
            message: 'Payment cancelled',
            severity: 'info'
          });
        }
      }
    };

    const razorpay = new window.Razorpay(options);
    
    razorpay.on('payment.success', (response) => {
      setPaymentOpen(false);
      setIsRegistered(true);
      setSnackbar({
        open: true,
        message: '🎉 Registration successful! You will receive the webinar details via email.',
        severity: 'success'
      });
    });

    razorpay.on('payment.error', (response) => {
      setSnackbar({
        open: true,
        message: 'Payment failed. Please try again.',
        severity: 'error'
      });
    });

    razorpay.open();
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Styled components
  const StyledBanner = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    height: isMobile ? '250px' : '400px',
    borderRadius: '16px',
    overflow: 'hidden',
    marginBottom: theme.spacing(3),
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '60%',
      background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
    }
  }));

  const StyledBadge = styled(Chip)(({ theme }) => ({
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 10,
    fontWeight: 'bold',
    padding: '4px 12px',
    fontSize: '0.9rem'
  }));

  const BannerContent = styled(Box)(({ theme }) => ({
    position: 'absolute',
    bottom: 24,
    left: 24,
    right: 24,
    zIndex: 5,
    color: 'white'
  }));

  return (
    <>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 1 }}>
            {webinar.title.length > 30 ? webinar.title.substring(0, 30) + '...' : webinar.title}
          </Typography>
          <IconButton onClick={() => setIsFavorite(!isFavorite)}>
            {isFavorite ? <Favorite color="error" /> : <FavoriteBorder />}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Zoom in={true} timeout={500}>
              <Box>
                {/* Banner Section */}
                <StyledBanner>
                  <img
                    src={webinar.image}
                    alt={webinar.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <StyledBadge
                    label={webinar.cost}
                    color={webinar.cost === 'Free' ? 'success' : 'primary'}
                  />
                  <BannerContent>
                    <Chip
                      icon={levelInfo.icon}
                      label={levelInfo.label}
                      sx={{
                        backgroundColor: levelInfo.color,
                        color: 'white',
                        mb: 1,
                        fontWeight: 'bold'
                      }}
                    />
                    <Typography
                      variant={isMobile ? 'h5' : 'h3'}
                      fontWeight="bold"
                      sx={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}
                    >
                      {webinar.title}
                    </Typography>
                    <Typography
                      variant={isMobile ? 'body2' : 'subtitle1'}
                      sx={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)', mt: 0.5 }}
                    >
                      {webinar.subtitle}
                    </Typography>
                  </BannerContent>
                </StyledBanner>

                {/* Tabs Section */}
                <Paper sx={{ mb: 3 }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant={isMobile ? 'scrollable' : 'fullWidth'}
                    scrollButtons={isMobile ? 'auto' : false}
                    allowScrollButtonsMobile
                  >
                    <Tab label="Overview" icon={<EventNote />} />
                    <Tab label="Topics" icon={<School />} />
                    <Tab label="Details" icon={<Info />} />
                  </Tabs>
                </Paper>

                {/* Tab Content */}
                <Box sx={{ mt: 2 }}>
                  {activeTab === 0 && (
                    <Fade in timeout={500}>
                      <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          About This Webinar
                        </Typography>
                        <Typography variant="body1" paragraph>
                          {webinar.description}
                        </Typography>

                        <Grid container spacing={2} sx={{ mt: 2 }}>
                          {[
                            { icon: <CalendarToday />, label: 'Date', value: webinar.date },
                            { icon: <AccessTime />, label: 'Time', value: webinar.time },
                            { icon: <LocationOn />, label: 'Location', value: webinar.location },
                            { icon: <Language />, label: 'Language', value: webinar.language },
                            { icon: <Person />, label: 'Speaker', value: webinar.speaker },
                            { icon: <Timer />, label: 'Duration', value: webinar.duration },
                            { icon: <Chair />, label: 'Seats Available', value: `${webinar.seatsAvailable} seats` },
                          ].map((item, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                              <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.light' }}>
                                  {item.icon}
                                </Avatar>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.label}
                                  </Typography>
                                  <Typography variant="body2" fontWeight="medium">
                                    {item.value}
                                  </Typography>
                                </Box>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Fade>
                  )}

                  {activeTab === 1 && (
                    <Fade in timeout={500}>
                      <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          What You'll Learn
                        </Typography>
                        <List>
                          {webinar.topics.map((topic, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <CheckCircle color="success" />
                              </ListItemIcon>
                              <ListItemText primary={topic} />
                            </ListItem>
                          ))}
                        </List>

                        <Grid container spacing={2} sx={{ mt: 3 }}>
                          <Grid item xs={12} sm={6}>
                            <Card>
                              <CardContent>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <PlayCircle color="primary" />
                                  <Typography variant="subtitle1">Recording</Typography>
                                </Box>
                                <Typography variant="body2">
                                  {webinar.recordingAvailable ? 
                                    'Available after session' : 
                                    'Not available'}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Card>
                              <CardContent>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <WorkspacePremium color="primary" />
                                  <Typography variant="subtitle1">Certificate</Typography>
                                </Box>
                                <Typography variant="body2">
                                  Certificate of participation
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Fade>
                  )}

                  {activeTab === 2 && (
                    <Fade in timeout={500}>
                      <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Additional Information
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2" color="primary">
                                Webinar Type
                              </Typography>
                              <Typography variant="body1">
                                {webinar.webinarType}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2" color="primary">
                                Level
                              </Typography>
                              <Chip
                                icon={levelInfo.icon}
                                label={levelInfo.label}
                                sx={{
                                  backgroundColor: levelInfo.color,
                                  color: 'white',
                                  mt: 1
                                }}
                              />
                            </Paper>
                          </Grid>
                          <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2" color="primary">
                                Seats Available
                              </Typography>
                              <Box display="flex" alignItems="center" gap={2} mt={1}>
                                <LinearProgress
                                  variant="determinate"
                                  value={(webinar.seatsAvailable / 100) * 100}
                                  sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                                />
                                <Typography variant="body2" fontWeight="bold">
                                  {webinar.seatsAvailable} seats left
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Fade>
                  )}
                </Box>
              </Box>
            </Zoom>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, position: 'sticky', top: 80 }}>
              <Typography variant="h5" gutterBottom align="center">
                Register Now
              </Typography>
              
              <Box textAlign="center" sx={{ mb: 3 }}>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  {webinar.cost}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  per person
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                {[
                  { icon: <CalendarToday />, label: 'Date', value: webinar.date },
                  { icon: <AccessTime />, label: 'Time', value: webinar.time },
                  { icon: <Timer />, label: 'Duration', value: webinar.duration },
                  { icon: <Chair />, label: 'Seats', value: `${webinar.seatsAvailable} left` },
                ].map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      py: 1,
                      borderBottom: index < 3 ? '1px solid' : 'none',
                      borderColor: 'divider'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {item.icon} {item.label}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleRegisterClick}
                disabled={isRegistered}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  background: isRegistered ? 'success.main' : 'primary.main'
                }}
              >
                {isRegistered ? '✓ Registered' : 'Register Now'}
              </Button>

              

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                Share this webinar
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                <IconButton color="primary"><WhatsApp /></IconButton>
                <IconButton color="info"><Twitter /></IconButton>
                <IconButton color="primary"><LinkedIn /></IconButton>
                <IconButton color="error"><Email /></IconButton>
                <IconButton><FileCopy /></IconButton>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Registration Dialog */}
      <Dialog
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Register for Webinar</Typography>
            <IconButton onClick={() => setRegisterOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {webinar.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  error={!!formErrors.phone}
                  helperText={formErrors.phone}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company/Organization (Optional)"
                  name="company"
                  value={formData.company}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Designation (Optional)"
                  name="designation"
                  value={formData.designation}
                  onChange={handleFormChange}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setRegisterOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRegisterSubmit}
            startIcon={<Payment />}
          >
            Proceed to Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 3,
            textAlign: 'center'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h5">Complete Payment</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 3 }}>
            <Typography variant="body1" paragraph>
              You are about to register for:
            </Typography>
            <Typography variant="h6" gutterBottom>
              {webinar.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {webinar.subtitle}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h4" color="primary" fontWeight="bold">
              {webinar.cost}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              per person
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                You will be redirected to Razorpay payment gateway
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={() => setPaymentOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            size="large"
            onClick={handlePayment}
            startIcon={<Payment />}
          >
            Pay Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WebinarDetails;